import React from "react";
import { useParams, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@components/ui/button";
import { endpoint, useAxiosCall } from "@services/api.services";
import { toast } from "sonner";
import { usePostData } from "@utils/query/index.query";
import { usePathname } from "@utils/hook/index.hook";

// Zod schema for form validation
const SourceSchema = z.object({
  name: z.string().min(1, "Source name is required"),
});

type SourceFormData = z.infer<typeof SourceSchema>;

const SourceForm: React.FC = () => {
  const queryClient = useQueryClient();
  const pathname = usePathname();

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SourceFormData>({
    resolver: zodResolver(SourceSchema),
    defaultValues: {
      name: "",
    },
  });

  // const { mutate } = usePostData();

  // Mutation for API call
  const mutation = useMutation({
    mutationFn: async (data: any) =>
      await useAxiosCall({
        endpoint: endpoint["managesource"],
        method: "POST",
        body: data,
      }),
    onSuccess: (data) => {
      console.log("Source submitted successfully:", data);

      toast.success("Source added successfully!", {
        closeButton: true,
        classNames: {
          description: "!text-black",
          closeButton: "!absolute !right-0",
          icon: "!mr-14",
        },
      });
      // Invalidate and refetch any related queries
      queryClient.invalidateQueries({ queryKey: ["sources"] });
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      // Reset form after successful submission
      reset();
    },
    onError: (error) => {
      console.error("Error submitting source:", error);
      toast.error("Failed to submit source. Please try again.", {
        closeButton: true,
        classNames: {
          description: "!text-black",
          closeButton: "!absolute !right-0",
          icon: "!mr-14",
        },
      });
    },
  });

  // Form submit handler
  const onSubmit = (data: SourceFormData) => {
    console.log("Submitting source data:", data);
    mutation.mutate(data);
  };

  return (
    <div className="max-w-md ml-2">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Source Name Field */}
        <div>
          <label
            htmlFor="sourceName"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Source Name
          </label>
          <input
            id="sourceName"
            type="text"
            {...register("name")}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter source name"
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={isSubmitting || mutation.isPending}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting || mutation.isPending
            ? "Submitting..."
            : "Submit Source"}
        </Button>
      </form>
    </div>
  );
};

export default SourceForm;
